import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { API_TYPE_GZ, WEB_COMPONENT_GZ_MOVE_TO_ACTION } from '../../../../../../webcomponents.constants';
import { ChipsGeneratorStrings } from '../../../../../../ui/gzChipsGeneratorInput/types';
import { translateFn } from '../../../../../../utils/translator.helper';
import { FontSizes } from '../../../../../../types/common/types';
import { NetworkTreeWebComponent } from '../../../../../networkTree';
import { getTimezone } from '../../../../../../utils/global.utils';
import { DestinationData, InvalidDestinationReason, MoveToPopUpStates, MoveToStates } from '../../types';
import { AffectedCompanyImportingInaccessibleMdrContacts } from '../../../../../../types/common/mdrTypes';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { NodeData } from '../../../../../../ui/gzTree/type';
import { NetworkPopUpEvents } from '../../../NetworkBasePopUp';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { LinkColor, LinkType } from '../../../../../../ui/gzLinkComponent/types';
import { GzToastNotificationWebComponent } from '../../../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../../../ui/gzToastNotification/type';
import {
  fetchAffectedCompaniesImportingInaccessibleMdrContacts,
  buildCsvForInaccessibleMdrContacts,
} from '../../../../../../utils/mdr.utils';

/**
 * @summary Type here the description of this component
 * @tag gz-move-to-action
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement(WEB_COMPONENT_GZ_MOVE_TO_ACTION)
export class GzMoveToActionWebComponent extends LitElement {
  @property({ type: Array })
  public items: Array<ProtectedEntity>;

  @property({ type: Object })
  public states: MoveToPopUpStates;

  @property({ type: Array })
  public uniqueParentIds: Array<string>;

  @property({ type: Boolean, reflect: false, attribute: false })
  public invalidDestination: boolean;

  @query('gz-network-tree')
  private readonly networkTree: NetworkTreeWebComponent;

  @state()
  private selectedDestinationText: string;

  @state()
  private destinationText: string;

  private invalidDestinationContent: TemplateResult;

  private infoMessage: string;

  private selectedDestinationTarget: { parentCompanyName: string; destinationName?: string };

  private selectedDestinationNode: DestinationData;

  private fullTreeNodesMap: Map<string, NodeData>;

  /**
   * A cache map that tracks child companies indirectly affected when an entity is moved to a destination target.
   * The impact is caused by importing inaccessible MDR contact details after the move.
   *
   * Key format: "movedEntityId,destinationTargetId"
   * Value: An array of affected company details.
   */
  private affectedCompaniesImportingInaccessibleMdrContacts: Map<
    string,
    Array<AffectedCompanyImportingInaccessibleMdrContacts>
  > = new Map();

  private allowedDestinationsForNetworkFolders: Array<number> = [
    Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP,
    Constants.ProtectedEntities.EPS.CUSTOM_GROUPS,
  ];

  private allowedDestinationsForNetworkEndpoints: Array<number> = [
    Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP,
    Constants.ProtectedEntities.EPS.CUSTOM_GROUPS,
    Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER,
  ];

  private allowedDestinationsForNetworkEndpointsMSP: Array<number> = [
    Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP,
    Constants.ProtectedEntities.EPS.CUSTOM_GROUPS,
  ];

  private allowedDestinationsForCompanyEntities: Array<number> = [
    Constants.Companies.TYPE_COMPANY_FOLDER,
    Constants.Companies.TYPE_COMPANIES_COLLECTION,
  ];

  private allowedDestinationsForContainerEntities: Array<number> = [
    Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER,
    Constants.ProtectedEntities.CONTAINERS.TYPE_CUSTOM_GROUPS,
  ];

  static get styles() {
    return styles;
  }

  firstUpdated(): void {
    this.networkTree.injectDependencies({
      api: { type: API_TYPE_GZ },
      translateFn: translateFn,
      getTimezone: getTimezone,
    });
    this.networkTree.start().then().catch();
  }

  render(): TemplateResult {
    if (!this.infoMessage) {
      this.setInfoMessage();
    }

    return html`<div class="wrapper">
      <div id="section-selected-items">
        <div id="chips-group">${this.renderChips()}</div>
        <div id="info-message-selected-items" class="pt-1">${unsafeHTML(this.infoMessage)}</div>
      </div>
      <div id="location-selector" class="d-flex flex-row location-selector pt-3 pb-1">
        ${this.renderSelectLocationTree()}
      </div>
      <div id="selected-targets">
        <div id="section-selected-targets-title" class="pb-1 mb-2 selection-target">
          <gz-title class="select-destination-text" fontWeight="600" fontSize="${FontSizes.PARAGRAPH_11}"
            >${translateFn('Destination')}</gz-title
          >
        </div>
        <div id="section-selected-targets-content">${this.renderSelectedTargetsContent()}</div>
      </div>
    </div>`;
  }

  private renderChips(): TemplateResult {
    const chips: Array<string> = this.items.map(item => item.name);

    return html`<gz-form-field id="selected-targets-label" label="${translateFn('Selected entities')}" disabled>
      <gz-chips-generator-input
        gz-form-input
        ?hideCopyToClipboardButton="${true}"
        ?disabled="${true}"
        ?required="${true}"
        ?themeInput="${true}"
        .strings="${this.getChipsGeneratorStrings()}"
        .chipList="${chips}"
      ></gz-chips-generator-input>
    </gz-form-field> `;
  }

  private renderSelectLocationTree(): TemplateResult {
    return html`<gz-text fontSize="14" class="pr-1 w-auto selection-tree-label">
        ${translateFn('Select destination:')}
      </gz-text>
      <gz-push-body-layout resizable="right" isOpen="true">
        <gz-network-tree @nodeChanged="${this.treeNodeChangedHandler}" slot="push-body-sidenav"></gz-network-tree>
      </gz-push-body-layout>`;
  }

  private renderSelectedTargetsContent(): TemplateResult {
    if (!this.selectedDestinationText) {
      return html`<gz-text class="font-italic" fontSize="${FontSizes.HEADING_6}">
        ${translateFn('The entities will be moved to the selected destination.')}</gz-text
      >`;
    }

    if (this.invalidDestination) {
      return this.invalidDestinationContent;
    }

    return html`<gz-text fontSize="${FontSizes.HEADING_6}">${this.renderSelectedDestination()}</gz-text>`;
  }

  private getChipsGeneratorStrings(): ChipsGeneratorStrings {
    return {
      label: translateFn('Selected entities'),
    };
  }

  private async treeNodeChangedHandler(event: CustomEvent): Promise<void> {
    this.selectedDestinationNode = event.detail.nodeInfo;
    this.fullTreeNodesMap = event.detail.fullTreeNodesMap;

    // Being an async function, must be called before prepareSelectedDestination which will add a render() call in queue
    await this.prepareAffectedCompaniesImportingInaccessibleMdrContacts();

    this.prepareSelectedDestination();
    this.resetInfoMessage();

    this.selectedDestinationNode.isValidMspCompany = false;

    if (this.uniqueParentIds.includes(this.selectedDestinationNode.targetId)) {
      this.toggleInvalidDestination(true, InvalidDestinationReason.ENTITIES_ALREADY_IN_SELECTED_DESTINATION);
      return;
    }

    if (Application.Main.getUser().companyInfo.specifics.rights.manageCompanies) {
      return this.handlePartnerDestinationTargetChanged();
    }

    return this.handleDestinationTargetChangedForNetworkEntities();
  }

  private handlePartnerDestinationTargetChanged() {
    if (this.states[MoveToStates.COMPANY_ENTITIES_SELECTED]) {
      return this.handleDestinationTargetChangedForCompanies();
    }

    return this.handlePartnerDestinationTargetChangedForNetworkEntities();
  }

  private handleDestinationTargetChangedForCompanies() {
    const destinationType = this.selectedDestinationNode.type;

    if (!this.allowedDestinationsForCompanyEntities.includes(destinationType)) {
      this.toggleInvalidDestination(true);
      this.invalidDestinationContent = this.getInvalidDestinationText(
        InvalidDestinationReason.INVALID_DESTINATION_COMPANY_CLASS_ENTITIES
      );
      return;
    }

    const selectedItemsIds: Array<string> = this.items.map((item: ProtectedEntity) => item._id);
    // check if destination is same as one of selected items (moving folder into self)
    if (selectedItemsIds.includes(this.selectedDestinationNode.targetId)) {
      this.toggleInvalidDestination(true, InvalidDestinationReason.INVALID_REASON_GENERIC);
      return;
    }

    if (this.isDestinationChildOfSelectedItems()) {
      this.toggleInvalidDestination(true, InvalidDestinationReason.DESTINATION_IS_CHILD_OF_PARENT);
      return;
    }

    if (this.hasSelectedIndirectlyCompaniesImportingInaccessibleMdrContacts()) {
      this.toggleInvalidDestination(true, InvalidDestinationReason.COMPANIES_IMPORTING_INACCESSIBLE_MDR_CONTACTS);
      return;
    }

    this.toggleInvalidDestination(false);
  }

  private handlePartnerDestinationTargetChangedForNetworkEntities() {
    const destinationCompany = this.selectedDestinationNode.companyId;

    if (!destinationCompany) {
      if (this.selectedDestinationNode.targetId === Application.Main.getUser().company.id) {
        this.toggleInvalidDestination(true, InvalidDestinationReason.INVALID_REASON_GENERIC);
        return;
      }
      throw new Error('Missing company id from destination node');
    }

    const selectedItemsCompanyId = this.items.map(item => item.company?.id).pop();

    if (!selectedItemsCompanyId) {
      console.error(`Could not get company id from selected items`);
      this.toggleInvalidDestination(true);
      return;
    }

    // destination is same as selected items company, we can proceed to move items
    if (destinationCompany === selectedItemsCompanyId) {
      return this.handleDestinationTargetChangedForNetworkEntities();
    }

    if (this.states[MoveToStates.UNMANAGED_ENTITIES_SELECTED]) {
      this.toggleInvalidDestination(true, InvalidDestinationReason.UNMANAGED_ENTITIES_SELECTED);
      return;
    }

    if (this.states[MoveToStates.ENTITIES_ALREADY_MOVING_SELECTED]) {
      this.toggleInvalidDestination(true, InvalidDestinationReason.ENTITIES_ALREADY_MOVING_SELECTED);
      return;
    }

    if (this.states[MoveToStates.ENTITIES_FROM_DELETED_IN_SELECTED_ITEMS]) {
      this.toggleInvalidDestination(true, InvalidDestinationReason.MOVE_FROM_DELETED_ANOTHER_COMPANY);
      return;
    }

    if (this.states[MoveToStates.CONTAINER_ENTITIES_SELECTED]) {
      this.toggleInvalidDestination(true, InvalidDestinationReason.MSP_MOVING_ENTITIES_NOT_ONLY_ENDPOINTS);
      return;
    }

    // we are not in MSP context, we can't move items to other companies
    if (!this.states[MoveToStates.TARGETS_ARE_FROM_MSP_COMPANY]) {
      this.toggleInvalidDestination(true, InvalidDestinationReason.NOT_MSP_CONTEXT);
      return;
    }

    const destinationCompanyNode = this.fullTreeNodesMap.get(destinationCompany);

    if (!destinationCompanyNode) {
      this.toggleInvalidDestination(true, InvalidDestinationReason.INVALID_REASON_GENERIC);
      return;
    }

    const destinationNotValidMSP = this.getDestinationNotValidMSP(destinationCompanyNode);

    if (destinationNotValidMSP) {
      this.toggleInvalidDestination(true, InvalidDestinationReason.NOT_MSP_CONTEXT);
      return;
    }

    // are we moving only endpoints?
    const endpointsToMove: Array<ProtectedEntity> = this.items.filter(
      (item: ProtectedEntity) => item.type === Constants.ProtectedEntities.EPS.TYPE_COMPUTER
    );

    if (endpointsToMove.length !== this.items.length) {
      this.toggleInvalidDestination(true, InvalidDestinationReason.MSP_MOVING_ENTITIES_NOT_ONLY_ENDPOINTS);
      return;
    }

    const destinationType = this.selectedDestinationNode.type;

    // check if destination is valid for network endpoints in MSP context
    if (!this.allowedDestinationsForNetworkEndpointsMSP.includes(destinationType)) {
      this.toggleInvalidDestination(true, InvalidDestinationReason.INVALID_DESTINATION_NETWORK_COMPUTERS_MSP);
      return;
    }

    this.selectedDestinationNode.isValidMspCompany = true;
    this.setInfoMessageMsp();
    this.toggleInvalidDestination(false);
  }

  private getDestinationNotValidMSP(destinationCompanyNode: NodeData): boolean {
    if (destinationCompanyNode.originalData.hasMspLicense !== undefined) {
      return !(
        destinationCompanyNode.originalData.hasMspLicense && !destinationCompanyNode.originalData.specifics.hasMspTrial
      );
    }

    const isInvalidMspDestination: boolean =
      !destinationCompanyNode.originalData.specifics.inheritedLicense ||
      !!destinationCompanyNode.originalData.specifics.hasMspTrial;

    return isInvalidMspDestination;
  }

  private handleDestinationTargetChangedForNetworkEntities() {
    const destinationType = this.selectedDestinationNode.type;

    if (this.states[MoveToStates.CONTAINER_ENTITIES_SELECTED]) {
      if (!this.allowedDestinationsForContainerEntities.includes(destinationType)) {
        this.toggleInvalidDestination(true, InvalidDestinationReason.INVALID_DESTINATION_CONTAINER_ENTITIES);
        return;
      }
    }

    if (this.states[MoveToStates.NETWORK_FOLDER_SELECTED]) {
      if (!this.allowedDestinationsForNetworkFolders.includes(destinationType)) {
        this.toggleInvalidDestination(true, InvalidDestinationReason.INVALID_DESTINATION_NETWORK_FOLDERS);
        return;
      }
      if (this.isDestinationChildOfSelectedItems()) {
        this.toggleInvalidDestination(true, InvalidDestinationReason.DESTINATION_IS_CHILD_OF_PARENT);
        return;
      }
    }
    if (this.states[MoveToStates.NETWORK_COMPUTERS_SELECTED]) {
      if (!this.allowedDestinationsForNetworkEndpoints.includes(destinationType)) {
        this.toggleInvalidDestination(true, InvalidDestinationReason.INVALID_DESTINATION_NETWORK_COMPUTERS);
        return;
      }
      if (this.isDestinationChildOfSelectedItems()) {
        this.toggleInvalidDestination(true, InvalidDestinationReason.DESTINATION_IS_CHILD_OF_PARENT);
        return;
      }
    }

    this.toggleInvalidDestination(false);
  }

  private prepareSelectedDestination(): void {
    if (!this.selectedDestinationNode.name) {
      throw new Error('Missing destination node name');
    }

    // destination node is own company
    if (this.selectedDestinationNode.targetId === Application.Main.getUser().company.id) {
      this.selectedDestinationTarget = {
        parentCompanyName: Application.Main.getUser().company.name,
        destinationName: undefined,
      };
      this.buildSelectedDestination();
      return;
    }

    // destination node is somewhere in the tree
    if (!this.selectedDestinationNode.companyId) {
      throw new Error('Missing company id from destination node');
    }

    const parentCompanyOfDestinationNode = this.fullTreeNodesMap.get(this.selectedDestinationNode.companyId);

    if (!parentCompanyOfDestinationNode) {
      throw new Error('Missing parent company of destination node in tree details');
    }

    this.selectedDestinationTarget = {
      parentCompanyName: parentCompanyOfDestinationNode.originalData.name,
      destinationName: this.selectedDestinationNode.name,
    };
    this.buildSelectedDestination();
  }

  private buildSelectedDestination(): void {
    const destinationName: string = this.selectedDestinationTarget.destinationName
      ? Translator.translateProtectedEntityNameByType(
          this.selectedDestinationTarget.destinationName,
          this.selectedDestinationNode.type,
          Constants.ProductTypes.GRAVITY_ZONE_CLOUD
        )
      : '';

    this.selectedDestinationText = this.selectedDestinationTarget.parentCompanyName;
    this.destinationText = destinationName;
  }

  private renderSelectedDestination(): TemplateResult {
    return html`${this.selectedDestinationText} / <b> ${this.destinationText} </b>`;
  }

  private toggleInvalidDestination(
    isInvalid: boolean,
    invalidReason: InvalidDestinationReason = InvalidDestinationReason.INVALID_REASON_GENERIC
  ): void {
    this.invalidDestination = isInvalid;
    this.invalidDestinationContent = this.getInvalidDestinationText(invalidReason);
    this.toggleConfirmButton(isInvalid);
  }

  private toggleConfirmButton(disabled: boolean) {
    this.dispatchEvent(
      new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
        bubbles: true,
        composed: true,
        detail: { value: disabled },
      })
    );
  }

  private isDestinationChildOfSelectedItems(): boolean {
    return this.items.some((item: ProtectedEntity) => {
      return this.selectedDestinationNode.path.includes(item._id);
    });
  }

  private hasSelectedIndirectlyCompaniesImportingInaccessibleMdrContacts(): boolean {
    return this.items.some((movedItem: ProtectedEntity) => {
      const key = movedItem._id + ',' + this.selectedDestinationNode.targetId;
      const affectedCompanies = this.affectedCompaniesImportingInaccessibleMdrContacts.get(key) ?? [];
      return affectedCompanies.length > 0;
    });
  }

  /**
   * Prepares a cache of affected companies that import inaccessible MDR contacts.
   * Explores newly selected entities to be moved and fetches updated data for them.
   *
   * @private
   */
  private async prepareAffectedCompaniesImportingInaccessibleMdrContacts(): Promise<void> {
    const unexploredEntities: Array<string> = [];

    this.items.forEach((movedItem: ProtectedEntity) => {
      const key = movedItem._id + ',' + this.selectedDestinationNode.targetId;
      if (!this.affectedCompaniesImportingInaccessibleMdrContacts.has(key)) {
        unexploredEntities.push(movedItem._id);
      }
    });

    if (unexploredEntities.length > 0) {
      await this.updateCacheAffectedCompaniesImportingInaccessibleMdrContacts(unexploredEntities);
    }
  }

  /**
   * Fetches the affected companies that import inaccessible MDR contacts after the move.
   * Updates the cache with the fetched data using the key format: (movedEntity, destination).
   *
   * @param entityIds The IDs of the moved entities
   * @private
   */
  private async updateCacheAffectedCompaniesImportingInaccessibleMdrContacts(entityIds: Array<string>): Promise<void> {
    try {
      const targetId = this.selectedDestinationNode.targetId;
      const response = await fetchAffectedCompaniesImportingInaccessibleMdrContacts(entityIds, targetId);

      Object.keys(response).forEach(movedEntityId => {
        const affectedCompanies = response[movedEntityId];
        const key = movedEntityId + ',' + this.selectedDestinationNode.targetId;
        this.affectedCompaniesImportingInaccessibleMdrContacts.set(key, affectedCompanies);
      });
    } catch (error) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: translateFn(
          'An unexpected error has occurred. Please try again. If the issue persists, contact Bitdefender Technical Support.'
        ),
        severity: ToastSeverity.ERROR,
      });
    }
  }

  public getDestination() {
    return this.selectedDestinationNode;
  }

  private getInvalidDestinationText(invalidDestinationReason: InvalidDestinationReason): TemplateResult {
    let gzTextContent: string;
    switch (invalidDestinationReason) {
      case InvalidDestinationReason.INVALID_DESTINATION_NETWORK_FOLDERS:
      case InvalidDestinationReason.INVALID_DESTINATION_NETWORK_COMPUTERS_MSP:
        gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
        ${translateFn(
          'Cannot move entities. Destination is not {OPEN_TAG} {folder_name} {CLOSE_TAG} or a network folder.',
          {
            OPEN_TAG: '<b>',
            CLOSE_TAG: '</b>',
            folder_name: Translator.translateProtectedEntityType(Constants.ProtectedEntities.EPS.CUSTOM_GROUPS),
          }
        )}
      </gz-text>`;
        break;
      case InvalidDestinationReason.INVALID_DESTINATION_CONTAINER_ENTITIES:
        gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
        ${translateFn(
          'Cannot move entities. Destination is not {OPEN_TAG} {folder_name} {CLOSE_TAG} or a container folder.',
          {
            OPEN_TAG: '<b>',
            CLOSE_TAG: '</b>',
            folder_name: Translator.translateProtectedEntityType(
              Constants.ProtectedEntities.CONTAINERS.TYPE_CUSTOM_GROUPS
            ),
          }
        )}
      </gz-text>`;
        break;
      case InvalidDestinationReason.INVALID_DESTINATION_NETWORK_COMPUTERS:
        gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
        ${translateFn(
          'Cannot move entities. Destination is not {OPEN_TAG} {first_folder} {CLOSE_TAG}, a network folder or {OPEN_TAG} {second_folder} {CLOSE_TAG}.',
          {
            OPEN_TAG: '<b>',
            CLOSE_TAG: '</b>',
            first_folder: Translator.translateProtectedEntityType(Constants.ProtectedEntities.EPS.CUSTOM_GROUPS),
            second_folder: Translator.translateProtectedEntityType(
              Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER
            ),
          }
        )}
      </gz-text>`;
        break;
      case InvalidDestinationReason.MSP_MOVING_ENTITIES_NOT_ONLY_ENDPOINTS:
        gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
          ${translateFn('Cannot move entities. Only endpoints can be moved to the selected destination.')}
        </gz-text>`;
        break;
      case InvalidDestinationReason.DESTINATION_IS_CHILD_OF_PARENT:
        gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
          ${translateFn(' Cannot move entities. Destination is a nested entity.')}
        </gz-text>`;
        break;
      case InvalidDestinationReason.INVALID_DESTINATION_COMPANY_CLASS_ENTITIES:
        gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
          ${translateFn(
            'Cannot move entities. Destination is not a {OPEN_TAG} {folder_name} {CLOSE_TAG} collection or a company folder.',
            {
              OPEN_TAG: '<b>',
              CLOSE_TAG: '</b>',
              folder_name: Translator.translateProtectedEntityType(Constants.Companies.TYPE_COMPANIES_COLLECTION),
            }
          )}
        </gz-text>`;
        break;
      case InvalidDestinationReason.MOVE_FROM_DELETED_ANOTHER_COMPANY:
        gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
          ${translateFn('Cannot move entities from {OPEN_TAG} {folder_name} {CLOSE_TAG} to another company.', {
            OPEN_TAG: '<b>',
            CLOSE_TAG: '</b>',
            folder_name: Translator.translateProtectedEntityType(Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER),
          })}
        </gz-text>`;
        break;
      case InvalidDestinationReason.ENTITIES_ALREADY_IN_SELECTED_DESTINATION:
        gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
          ${translateFn(
            'Cannot move entities. At least one of the selected entities already exists in the same destination.'
          )}
        </gz-text>`;
        break;
      case InvalidDestinationReason.UNMANAGED_ENTITIES_SELECTED:
        gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
          ${translateFn('Cannot move entities. Your selection includes at least one unmanaged endpoint.')}
        </gz-text>`;
        break;
      case InvalidDestinationReason.ENTITIES_ALREADY_MOVING_SELECTED:
        gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
          ${translateFn(
            'Cannot move entities. Your selection includes at least one endpoint that is in the process of being moved or has already been moved.'
          )}
        </gz-text>`;
        break;
      case InvalidDestinationReason.COMPANIES_IMPORTING_INACCESSIBLE_MDR_CONTACTS:
        const csvContent = this.getCsvContentForAffectedMdrCompanies();
        gzTextContent = `<gz-text fontSize="${FontSizes.PARAGRAPH_13}">
          ${translateFn(
            'Cannot move entities. Your selection includes companies using MDR contact details from partners that would no longer be accessible. To proceed, select a different destination. Alternatively, provide new MDR contacts for the affected companies in {OPEN_TAG}this downloadable CSV.{CLOSE_TAG} Once done, try again.',
            {
              OPEN_TAG: `<gz-link href="${csvContent}" download="companies-mdr.csv">`,
              CLOSE_TAG: '</gz-link>',
            }
          )}
        </gz-text>`;
        break;
      case InvalidDestinationReason.NOT_MSP_CONTEXT:
      case InvalidDestinationReason.INVALID_REASON_GENERIC:
      default:
        gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
          ${translateFn('Cannot move entities. Destination is invalid.')}
        </gz-text>`;
    }

    return html`<div class="d-flex flex-row align-items-center error-notification">
      <gz-icon class="pr-1 notification-negative-icon" name="ds-icon-notification_negative" color="negative"></gz-icon
      >${unsafeHTML(gzTextContent)}
    </div>`;
  }

  private getCsvContentForAffectedMdrCompanies(): string {
    const affectedCompanies: Array<AffectedCompanyImportingInaccessibleMdrContacts> = [];

    this.items.forEach((movedItem: ProtectedEntity) => {
      const key = movedItem._id + ',' + this.selectedDestinationNode.targetId;
      const affectedChildren = this.affectedCompaniesImportingInaccessibleMdrContacts.get(key) ?? [];
      affectedCompanies.push(...affectedChildren);
    });

    return buildCsvForInaccessibleMdrContacts(affectedCompanies);
  }

  private setInfoMessageMsp(): void {
    const link = 'https://www.bitdefender.com/business/support/en/77209-1081513-moving-entities.html ';
    this.infoMessage = `<gz-text fontSize="${FontSizes.HEADING_6}">
          ${translateFn(
            'The entities will be moved to the selected destination. All endpoints will inherit the policies of the target company. For details about moving entities, refer to {OPEN_LINK_TAG} {OPEN_SPAN_TAG} this article. {CLOSE_SPAN_TAG} {CLOSE_LINK_TAG}',
            {
              OPEN_LINK_TAG: `<gz-link fontSize="12" href="${link}" color="${LinkColor.PRIMARY}" type="${LinkType.UNDERLINE}">`,
              CLOSE_LINK_TAG: '</gz-link>',
              OPEN_SPAN_TAG: '<span>',
              CLOSE_SPAN_TAG: '</span>',
            }
          )}
          </gz-text>`;
  }

  private setInfoMessage(): void {
    if (!Application.Main.company.isPartner() || this.states[MoveToStates.NETWORK_ENTITIES_SELECTED]) {
      const link = 'https://www.bitdefender.com/business/support/en/77209-1081513-moving-entities.html';
      this.infoMessage = `<gz-text fontSize="${FontSizes.HEADING_6}">
          ${translateFn(
            'The entities will be moved to the selected destination. For details about moving entities, refer to {OPEN_LINK_TAG} {OPEN_SPAN_TAG} this article. {CLOSE_SPAN_TAG} {CLOSE_LINK_TAG}',
            {
              OPEN_LINK_TAG: `<gz-link fontSize="12" href="${link}" color="${LinkColor.PRIMARY}" type="${LinkType.UNDERLINE}">`,
              CLOSE_LINK_TAG: '</gz-link>',
              OPEN_SPAN_TAG: '<span>',
              CLOSE_SPAN_TAG: '</span>',
            }
          )}
        </gz-text>`;
      return;
    }

    if (this.states[MoveToStates.COMPANY_ENTITIES_SELECTED]) {
      const link = 'https://www.bitdefender.com/business/support/en/77209-1081513-moving-entities.html';
      this.infoMessage = `<gz-text fontSize="${FontSizes.HEADING_6}">
          ${translateFn(
            'The entities will be moved to the selected destination. When moving companies, avoid taking any actions on them until they appear in their new location. For details about moving entities, refer to {OPEN_LINK_TAG} {OPEN_SPAN_TAG} this article. {CLOSE_SPAN_TAG} {CLOSE_LINK_TAG}',
            {
              OPEN_LINK_TAG: `<gz-link fontSize="12" href="${link}" color="${LinkColor.PRIMARY}" type="${LinkType.UNDERLINE}">`,
              CLOSE_LINK_TAG: '</gz-link>',
              OPEN_SPAN_TAG: '<span>',
              CLOSE_SPAN_TAG: '</span>',
            }
          )}
          </gz-text>`;
      return;
    }
  }

  private resetInfoMessage() {
    this.infoMessage = '';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_GZ_MOVE_TO_ACTION: GzMoveToActionWebComponent;
  }
}
